import {useReducedMotion} from '@mantine/hooks';
import {useEffect, useRef, useState} from 'react';

// Drain the current backlog over roughly this window rather than all at once. The reveal rate is
// proportional to how far behind we are, so it self-tunes to the stream's arrival rate: at steady
// state it holds a ~(arrival rate * this window) buffer and reveals continuously at the arrival
// rate, instead of dumping each burst in a frame or two and then stalling until the next burst.
const REVEAL_WINDOW_SEC = 0.3;
// A gentle floor so the final few words don't crawl once the stream ends and the backlog is tiny.
// Kept well below a typical arrival rate so it doesn't cause the reveal to outrun the stream and
// stall between bursts.
const MIN_WORDS_PER_SEC = 14;

// Split into word tokens that each carry their trailing whitespace, so slicing to a prefix and
// re-joining reproduces the original text exactly and never cuts inside a word. A <bnch_id> chip
// tag contains no spaces, so it is always revealed whole rather than as broken markup.
export function splitWordsWithWhitespace(text: string): Array<string> {
  return text.match(/\s*\S+\s*/g) ?? [];
}

// How many (fractional) words to advance given how far behind we are and how much time elapsed.
// Pure so the cadence is unit-testable without a running animation frame.
export function wordsToAdvance(backlog: number, elapsedMs: number): number {
  if (backlog <= 0) {
    return 0;
  }
  const seconds = elapsedMs / 1000;
  const wordsPerSec = Math.max(MIN_WORDS_PER_SEC, backlog / REVEAL_WINDOW_SEC);
  return wordsPerSec * seconds;
}

/**
 * Smooths streamed text so it flows in word-by-word instead of jumping in bursts. The backend
 * flushes the omnibox direct answer roughly every 200ms, so without smoothing several words appear
 * at once. The reveal catches up when it falls behind so it never lags meaningfully behind the true
 * text, and settles exactly on the final text once streaming stops.
 *
 * Respects prefers-reduced-motion by rendering the full text immediately.
 */
export function useSmoothedText(target: string): string {
  const reducedMotion = useReducedMotion();
  // Show whatever text is present on first mount immediately (no empty flash / typewriter-from-zero);
  // the smoothing kicks in on subsequent streamed bursts, which is where the bursting was visible.
  const [count, setCount] = useState(() => splitWordsWithWhitespace(target).length);

  const targetRef = useRef(target);
  const countRef = useRef(count);
  countRef.current = count;
  const carryRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);

  useEffect(() => {
    const words = splitWordsWithWhitespace(target);
    const shownPrefix = splitWordsWithWhitespace(targetRef.current).slice(0, countRef.current).join('');
    targetRef.current = target;

    // Replacement (new query) vs extension (more streamed text): if the new target no longer starts
    // with what we've already revealed, restart the reveal from the beginning.
    if (!target.startsWith(shownPrefix)) {
      carryRef.current = 0;
      countRef.current = 0;
      setCount(0);
    }

    if (reducedMotion || typeof requestAnimationFrame !== 'function') {
      carryRef.current = 0;
      countRef.current = words.length;
      setCount(words.length);
      return undefined;
    }

    if (rafRef.current != null || countRef.current >= words.length) {
      // A loop is already running (it reads the latest target via the ref), or there is nothing new
      // to reveal.
      return undefined;
    }

    lastTsRef.current = null;
    const tick = (ts: number): void => {
      const total = splitWordsWithWhitespace(targetRef.current).length;
      const last = lastTsRef.current;
      lastTsRef.current = ts;
      if (last != null) {
        carryRef.current += wordsToAdvance(total - countRef.current, ts - last);
        const step = Math.floor(carryRef.current);
        if (step > 0) {
          carryRef.current -= step;
          const next = Math.min(total, countRef.current + step);
          if (next !== countRef.current) {
            countRef.current = next;
            setCount(next);
          }
        }
      }
      if (countRef.current < total) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        lastTsRef.current = null;
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return undefined;
  }, [target, reducedMotion]);

  useEffect(
    () => () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    []
  );

  const words = splitWordsWithWhitespace(target);
  return words.slice(0, Math.min(count, words.length)).join('');
}
